import { ArrowRight, Globe2, Mail, MapPin, Phone } from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { contactConfig, type Distributor } from "@/lib/contact-config";
import { localizeUrl, type Locale } from "@/lib/site";
import worldMapUrl from "@/assets/world-map.svg";

type Region = {
  code: string;
  flag: string;
  name: RegionLabelKey;
};

const REGIONS: Record<string, Region> = {
  UA: { code: "UA", flag: "🇺🇦", name: "distributorRegionUkraine" },
  PL: { code: "PL", flag: "🇵🇱", name: "distributorRegionPoland" },
  SK: { code: "SK", flag: "🇸🇰", name: "distributorRegionSlovakia" },
  MD: { code: "MD", flag: "🇲🇩", name: "distributorRegionMoldova" },
  EU_OPERATIONS: { code: "EU_OPERATIONS", flag: "🇪🇺", name: "distributorRegionEuOperations" },
  GLOBAL: { code: "GLOBAL", flag: "🌍", name: "distributorRegionGlobal" },
};

type RegionLabelKey =
  | "distributorRegionUkraine"
  | "distributorRegionPoland"
  | "distributorRegionSlovakia"
  | "distributorRegionMoldova"
  | "distributorRegionEuOperations"
  | "distributorRegionGlobal";

function resolveRegion(address: string): Region {
  const a = address.toLowerCase();
  if (a.includes("україна") || a.includes("ukraine")) return REGIONS.UA;
  if (a.includes("poland") || a.includes("polska")) return REGIONS.PL;
  if (a.includes("slovakia") || a.includes("slovensko")) return REGIONS.SK;
  if (a.includes("moldova")) return REGIONS.MD;
  return REGIONS.GLOBAL;
}

function localizeDistributor(distributor: Distributor, locale: Locale): Distributor {
  const localized = distributor.localized?.[locale];
  return localized ? { ...distributor, ...localized } : distributor;
}

function FlagBadge({ region, label }: { region: Region; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/80 px-2.5 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm">
      <span aria-hidden className="text-sm leading-none">
        {region.flag}
      </span>
      {label}
    </span>
  );
}

function WorldMapBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-16 bottom-0 overflow-hidden"
    >
      {/* Faded world map silhouette */}
      <div
        className="absolute left-1/2 top-[34vh] aspect-[95/62] w-[min(1400px,115vw)] -translate-x-1/2 -translate-y-1/2 opacity-[0.06] text-foreground"
        style={{
          WebkitMaskImage: "radial-gradient(ellipse at center, black 38%, transparent 76%)",
          maskImage: "radial-gradient(ellipse at center, black 38%, transparent 76%)",
          WebkitMaskComposite: "source-in",
        }}
      >
        <img
          src={worldMapUrl}
          alt=""
          className="h-full w-full object-contain"
          style={{
            filter: "brightness(0) saturate(100%) invert(var(--map-invert, 0))",
          }}
        />
      </div>
      {/* Dotted grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.18] text-muted-foreground"
        style={{
          backgroundImage: "radial-gradient(currentColor 1px, transparent 1.2px)",
          backgroundSize: "22px 22px",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 34vh, black 26%, transparent 68%)",
          maskImage: "radial-gradient(ellipse at 50% 34vh, black 26%, transparent 68%)",
        }}
      />
    </div>
  );
}

function DistributorRow({
  distributor,
  labels,
  region,
  regionLabel,
}: {
  distributor: Distributor;
  labels: { address: string; phone: string; email: string; website: string };
  regionLabel: string;
  region: Region;
}) {
  return (
    <article className="group relative rounded-2xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm transition-colors hover:bg-card sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <FlagBadge region={region} label={regionLabel} />
          <h2 className="mt-3 text-xl font-semibold tracking-tight sm:text-2xl">
            {distributor.name}
          </h2>
        </div>
      </div>
      {distributor.description && (
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {distributor.description}
        </p>
      )}
      <div className="mt-6 grid gap-4 text-sm text-muted-foreground sm:grid-cols-2">
        <div className="flex gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="font-medium text-foreground">{labels.address}</p>
            <p className="mt-1">{distributor.address}</p>
          </div>
        </div>
        {distributor.phones && (
          <div className="flex gap-2">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-foreground">{labels.phone}</p>
              {distributor.phones.map((phone) => (
                <a
                  className="mt-1 block hover:text-foreground"
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  key={phone}
                >
                  {phone}
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="font-medium text-foreground">{labels.email}</p>
            <a className="mt-1 block hover:text-foreground" href={`mailto:${distributor.email}`}>
              {distributor.email}
            </a>
          </div>
        </div>
        {distributor.website && (
          <div className="flex gap-2">
            <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-foreground">{labels.website}</p>
              <a
                className="mt-1 block hover:text-foreground"
                href={distributor.website.href}
                rel="noopener noreferrer"
                target="_blank"
              >
                {distributor.website.label}
              </a>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export function DistributorsPage() {
  const { site, messages, href } = useSite();
  const config = contactConfig[site.market];
  const contactSales = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const labels = {
    address: messages.contactAddressLabel,
    phone: messages.contactPhoneLabel,
    email: messages.contactEmailLabel,
    website: messages.contactWebsiteLabel,
  };
  const regionLabels: Record<RegionLabelKey, string> = {
    distributorRegionUkraine: messages.distributorRegionUkraine,
    distributorRegionPoland: messages.distributorRegionPoland,
    distributorRegionSlovakia: messages.distributorRegionSlovakia,
    distributorRegionMoldova: messages.distributorRegionMoldova,
    distributorRegionEuOperations: messages.distributorRegionEuOperations,
    distributorRegionGlobal: messages.distributorRegionGlobal,
  };

  const entries =
    config.variant === "exclusive-distributor"
      ? [
          {
            distributor: localizeDistributor(config.distributor, site.locale),
            region: resolveRegion(config.distributor.address),
          },
        ]
      : config.distributors.map((d) => ({
          distributor: localizeDistributor(d, site.locale),
          region: resolveRegion(d.address),
        }));
  const otherEntries =
    config.variant === "exclusive-distributor"
      ? config.otherDistributors.map((d) => ({
          distributor: localizeDistributor(d, site.locale),
          region: resolveRegion(d.address),
        }))
      : [];
  const directSalesEntry = config.directSalesContact
    ? {
        distributor: localizeDistributor(config.directSalesContact, site.locale),
        region: REGIONS.EU_OPERATIONS,
      }
    : undefined;

  return (
    <SiteLayout>
      <PageBreadcrumb items={[{ label: messages.footerLinkDistributors }]} />
      <section className="relative overflow-hidden">
        <WorldMapBackdrop />
        <div className="relative z-10 mx-auto max-w-3xl px-6 py-20 animate-in fade-in slide-in-from-bottom-2 duration-500 sm:py-28">
          <div className="text-center">
            <p className="text-sm font-medium text-primary">{messages.distributorsEyebrow}</p>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
              {messages.distributorsHeading}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">{messages.distributorsIntro}</p>
          </div>

          <h2 className="mt-14 mb-6 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            {config.variant === "exclusive-distributor" && site.market === "ukraine"
              ? messages.exclusiveDistributorTitle
              : site.market === "global"
                ? messages.globalDistributorListTitle
                : messages.distributorListTitle}
          </h2>
          <div className="space-y-4">
            {entries.map(({ distributor, region }) => (
              <DistributorRow
                key={distributor.name}
                distributor={distributor}
                region={region}
                regionLabel={regionLabels[region.name]}
                labels={labels}
              />
            ))}
          </div>

          {otherEntries.length > 0 && (
            <>
              <h2 className="mt-14 mb-6 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {messages.otherRegionsDistributorTitle}
              </h2>
              <div className="space-y-4">
                {otherEntries.map(({ distributor, region }) => (
                  <DistributorRow
                    key={distributor.name}
                    distributor={distributor}
                    region={region}
                    regionLabel={regionLabels[region.name]}
                    labels={labels}
                  />
                ))}
              </div>
            </>
          )}

          {directSalesEntry && (
            <>
              <h2 className="mt-14 mb-6 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {messages.directSalesOperationsTitle}
              </h2>
              <div className="space-y-4">
                <DistributorRow
                  distributor={directSalesEntry.distributor}
                  region={directSalesEntry.region}
                  regionLabel={regionLabels[directSalesEntry.region.name]}
                  labels={labels}
                />
              </div>
            </>
          )}

          <div className="mt-10 overflow-hidden rounded-2xl border border-border/60 bg-card/80 p-6 shadow-sm backdrop-blur-sm sm:p-7">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 opacity-40"
              style={{ background: "var(--gradient-aurora)" }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
            />
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight">
                  {messages.distributorContactCtaTitle}
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  {messages.distributorContactCtaBody}
                </p>
              </div>
              <Button asChild className="shrink-0">
                <a href={contactSales}>
                  {messages.distributorContactCtaButton}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
